import React from "react";
import PT from "prop-types";
import { useForm, FormContext } from "react-hook-form";
import { AbstractControlledField, TextField } from "_c/form/Field";
import ButtonGroup from "_c/form/ButtonGroup";
import { Button } from "_c/button";
import { useCloudinaryWidget } from "@/hooks/useCloudinaryWidget";
import { ORDERS_NS as ns } from "@/i18n/ns";
import { generateUrl } from "@/utils/cloudinary";
import CameraIcon from "_c/icon/Camera";
import { SubmitButton } from "../styled";
import { useQuery } from "react-query";
import { TaskClaimsCacheService } from "@/services/taskClaims/cache";
import QueryRequest from "@/components/QueryRequest";
import { useMut } from "@/hooks/useMut";
import { TaskClaimsService } from "@/services/taskClaims";
import { Content } from "_c/Modals";
import isempty from "lodash/isEmpty";
import { TasksAPI } from "@/api/tasks";
import { CircleButton } from "_c/button";
import TrashIcon from "@/components/icon/Trash";

const MAX_PHOTOS = 5;
const UploadField = ({ onChange, value, t, ...props }) => {
  const { uploadPhoto } = useCloudinaryWidget();
  const onClick = async () => {
    const photo = await uploadPhoto({ multiple: false });
    if (photo) {
      onChange([...value, photo]);
    }
  };
  const removePhoto = (photo) => {
    onChange(value.filter((p) => p !== photo));
  };
  return (
    <>
      <Button
        aria-label={t("common:add_photo")}
        onClick={onClick}
        withIcon
        disabled={value.length >= MAX_PHOTOS}
        medium
        {...props}
        type="button"
      >
        <CameraIcon color="white" />
        <span>{t("common:add_photo")}</span>
      </Button>
      {!isempty(value) && (
        <ul
          css={`
            margin-top: 10px;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
          `}
        >
          {value.map((photo) => (
            <li
              css={`
                position: relative;
                width: calc(33.333% - 2px);
                padding-bottom: calc(33.333% - 2px);
                margin: 1px;
              `}
              key={photo}
            >
              <img
                key={photo}
                css={`
                  position: absolute;
                  left: 0;
                  top: 0;
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                  border-radius: var(--root-br);
                `}
                src={generateUrl(photo, {
                  width: 300,
                  height: 300,
                  crop: "fill",
                })}
              />
              <CircleButton
                css={`
                  position: absolute;
                  top: 7px;
                  right: 7px;
                `}
                onClick={() => removePhoto(photo)}
                medium
                negative
                title={t("common:remove_photo")}
              >
                <TrashIcon width="20" />
              </CircleButton>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};
const TaskDispute = ({ t, task, claim, ...props }) => {
  const methods = useForm({
    defaultValues: {
      reason: claim?.reason || "",
      photos: claim?.photos || [],
    },
  });
  const { handleSubmit, formState } = methods;
  const createUpdateDisputeMut = useMut(
    (form) => {
      if (claim) {
        return TaskClaimsService.updateTaskClaim(claim._id, form);
      } else {
        return TaskClaimsService.createTaskClaim(task._id, form);
      }
    },
    {
      t,
      notifyOnError: true,
      onSuccess: props.onClose,
      toast: t(
        `${ns}:dispute_form.${claim ? "update_success" : "submit_success"}`
      ),
    }
  );
  const { dirty } = formState;
  const onSubmit = async (form) => createUpdateDisputeMut.mutate(form);
  return (
    <Content
      css={`
        color: var(--font-color-main);
      `}
    >
      <FormContext {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            t={t}
            as="textarea"
            rows="8"
            required
            maxLength="4096"
            name="reason"
            id="dispute_reason"
            label={t(`${ns}:dispute_form.reason`)}
          />
          {/*
          <FormSect>
            <Button withIcon onClick={onPhotoClick} type="button">
              <CameraIcon color="white" />
              <span>{t("common:photo")}</span>
            </Button>
          </FormSect>
          */}
          <AbstractControlledField
            t={t}
            label={t("common:photo")}
            name="photos"
            id="portfolio-upload__photo"
            Component={UploadField}
          />

          <ButtonGroup>
            <SubmitButton disabled={createUpdateDisputeMut.isLoading || !dirty}>
              {t("common:submit")}
            </SubmitButton>
          </ButtonGroup>
        </form>
      </FormContext>
    </Content>
  );
};
TaskDispute.propTypes = {
  task: PT.object.isRequired,
  onClose: PT.func.isRequired,
  t: PT.func.isRequired,
};
const TaskDisputeWithQueryRequest = (props) => {
  const isQueryEnabled = !!props.task.claim;
  const query = useQuery(
    TaskClaimsCacheService.buildSingleTaskClaimQueryKey(props.task.claim),
    () => TasksAPI.getDisputeById(props.task.claim),
    {
      enabled: isQueryEnabled,
      refetchInterval: false,
    }
  );
  const render = () => {
    return <TaskDispute {...props} claim={query.data} />;
  };
  if (isQueryEnabled) {
    return <QueryRequest query={query}>{render()}</QueryRequest>;
  } else {
    return render();
  }
};
export default TaskDisputeWithQueryRequest;
